/**
 * Niimbot BLE binary printer driver — direct byte-level protocol.
 *
 * Talks straight to Niimbot B1, B21, B31 and B3S over Web Bluetooth using
 * the printer's own binary protocol — no Niimbot Studio driver, no OS
 * printer registration, no system print dialog needed. The whole print
 * pipeline lives inside this file so the modal can just call
 * `printBitmap(printer, canvas, copies)`.
 *
 * Protocol reference (published by NiimPrintX / niimprint communities):
 *
 *   Frame:  0x55 0x55 | cmd(1) | len(1) | payload(len) | chksum(1) | 0xAA 0xAA
 *   chksum = XOR of (cmd XOR len XOR payload[0] XOR ... XOR payload[len-1])
 *
 *   Commands used here:
 *     0x21 → set density   payload = [density: 1..5]
 *     0x23 → set label type payload = [1 = gap paper]
 *     0x01 → start print    payload = [0x01]
 *     0x03 → start page     payload = [0x01]
 *     0x13 → set dimension  payload = [width_hi, width_lo, height_hi, height_lo]
 *     0x15 → set quantity   payload = [copies_hi, copies_lo]
 *     0x85 → row image data payload = [row_hi, row_lo, blackCount, ...bytes]
 *     0x84 → blank row(s)   payload = [row_hi, row_lo, count, 0x01]
 *     0xE3 → end page       payload = [0x01]
 *     0xF3 → end print      payload = [0x01]
 *
 * The B-series head is 12 dots/mm ≈ 203 DPI. Head width varies per model:
 *     B1  =  96 px = 12 mm
 *     B21 = 384 px = 50 mm  (label max)
 *     B31 = 384 px = 50 mm
 *     B3S = 240 px = 30 mm
 *
 * At 203 DPI a 75×25 mm ring tag is 600×200 px — well inside every model.
 * We just clip the bitmap width to the printer's head width so a B3S doesn't
 * try to print a 600-px-wide row (it would truncate silently on-device).
 */

// GATT identifiers (same across the whole B-series).
export const NIIMBOT_SERVICE_UUID = "e7810a71-73ae-499d-8c15-faa9aef0c3f2";
export const NIIMBOT_CHAR_UUID = "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f";

// Head width in pixels — clip images so we never overrun the head.
const HEAD_WIDTH_PX = {
  B1: 96,
  B21: 384,
  B31: 384,
  B3S: 240,
  B3: 384,
  D11: 96,
  D110: 96,
  D101: 96,
  M2: 384,
  M3: 384,
};
const DEFAULT_HEAD_WIDTH = 384;

// -------- Packet helpers --------

function encodePacket(cmd, payload) {
  const len = payload.length;
  let checksum = cmd ^ len;
  for (const b of payload) checksum ^= b;
  return new Uint8Array([0x55, 0x55, cmd, len, ...payload, checksum & 0xff, 0xaa, 0xaa]);
}

function u16be(n) {
  return [(n >> 8) & 0xff, n & 0xff];
}

// -------- Public driver API --------

/**
 * Connect to a paired Niimbot printer and hand back a driver handle.
 * The handle exposes `.print(canvas, copies, options)` and `.disconnect()`.
 */
export async function connect(device) {
  if (!device?.gatt) throw new Error("No BLE device");
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(NIIMBOT_SERVICE_UUID);
  const characteristic = await service.getCharacteristic(NIIMBOT_CHAR_UUID);

  // Enable notifications so responses come back. Some models complete
  // silently but subscribing is harmless when it's not needed.
  try {
    await characteristic.startNotifications();
  } catch (_e) {
    // Non-notifying models — write-without-response is still fine.
  }

  const send = async (packet) => {
    // `writeValueWithoutResponse` avoids ATT roundtrip stall on the tight
    // per-row loop. `writeValue` falls back for firmware that requires ACKs.
    try {
      await characteristic.writeValueWithoutResponse(packet);
    } catch (_e) {
      await characteristic.writeValue(packet);
    }
  };

  return {
    device,
    server,
    service,
    characteristic,
    send,
    disconnect: () => { try { device.gatt.disconnect(); } catch { /* already gone */ } },
    print: (canvas, copies, options) => printBitmap({ send }, canvas, copies, options),
  };
}

// -------- Bitmap → protocol --------

/**
 * Encode an off-screen canvas into a monochrome bitmap and stream it to
 * the printer as Niimbot 0x85 rows (or 0x84 blank-row runs where an entire
 * scan line is white — saves ~40 % airtime on jewelry tags which have lots
 * of empty space).
 *
 * Threshold uses a simple luma cutoff (128); the label templates already
 * use pure-black text on white, so no dithering is needed.
 */
export async function printBitmap({ send }, canvas, copies = 1, options = {}) {
  const model = (options.model || "B21").toUpperCase();
  const density = Math.max(1, Math.min(5, options.density ?? 3));
  const labelType = options.labelType ?? 1; // 1 = die-cut gap paper (Niimbot jewelry rolls)

  const headWidth = HEAD_WIDTH_PX[model] || DEFAULT_HEAD_WIDTH;

  // Clip canvas width to the printer head (rows longer than the head
  // silently truncate — a common cause of "printed half the label" bugs).
  const srcW = canvas.width;
  const srcH = canvas.height;
  const printW = Math.min(srcW, headWidth);
  // Widths MUST be a multiple of 8 because we pack 8 pixels/byte.
  const padded = Math.ceil(printW / 8) * 8;

  const ctx = canvas.getContext("2d");
  const img = ctx.getImageData(0, 0, printW, srcH);

  // -------- 1. Preamble commands --------
  await send(encodePacket(0x21, [density]));       // set density
  await send(encodePacket(0x23, [labelType]));     // set label type
  await send(encodePacket(0x01, [0x01]));          // start print
  await send(encodePacket(0x03, [0x01]));          // start page
  // Dimensions in *pixels* (protocol reads them as u16-big-endian).
  await send(encodePacket(0x13, [...u16be(padded), ...u16be(srcH)]));
  await send(encodePacket(0x15, [...u16be(Math.max(1, copies))]));

  // -------- 2. Image rows --------
  // Walk each row. If the entire row is white → emit one 0x84 packet (or
  // extend a running blank count). Otherwise pack 8-pixel-per-byte and
  // emit 0x85.
  const bytesPerRow = padded / 8;
  let blankRunStart = -1;
  let blankRunCount = 0;

  const flushBlankRun = async () => {
    if (blankRunCount === 0) return;
    // Niimbot's blank-row packet counts up to 255 in one shot.
    while (blankRunCount > 0) {
      const chunk = Math.min(255, blankRunCount);
      await send(encodePacket(0x84, [...u16be(blankRunStart), chunk, 0x01]));
      blankRunStart += chunk;
      blankRunCount -= chunk;
    }
    blankRunStart = -1;
    blankRunCount = 0;
  };

  const rowBytes = new Uint8Array(bytesPerRow);
  // Yield to the BLE stack every N rows so notification / write-completion
  // events can drain; without this, Chrome's write queue can back up on
  // 200-row jobs and drop trailing packets silently.
  const YIELD_EVERY = 16;
  for (let y = 0; y < srcH; y += 1) {
    // Fast scan: count non-white pixels to decide blank-vs-image.
    let blackCount = 0;
    rowBytes.fill(0);
    for (let x = 0; x < printW; x += 1) {
      const idx = (y * printW + x) * 4;
      // Luma = 0.299·R + 0.587·G + 0.114·B (Rec. 601). Anything darker
      // than mid-grey becomes a black dot on paper.
      const r = img.data[idx], g = img.data[idx + 1], b = img.data[idx + 2];
      const luma = (299 * r + 587 * g + 114 * b) / 1000;
      if (luma < 128) {
        rowBytes[x >> 3] |= 0x80 >> (x & 7);
        blackCount += 1;
      }
    }

    if (blackCount === 0) {
      // Extend running blank block
      if (blankRunStart === -1) blankRunStart = y;
      blankRunCount += 1;
      continue;
    }
    // Flush any pending blanks first so row-index sequencing stays correct.
    await flushBlankRun();

    // 0x85 packet header: [rowIdx_hi, rowIdx_lo, blackDots_count, ...rowBytes]
    // blackDots is a "hint" the printer uses to skip empty tail bytes —
    // capping at 255 is safe (older firmware treats larger values as 255).
    const hint = Math.min(255, blackCount);
    const packet = new Uint8Array(3 + bytesPerRow);
    packet[0] = (y >> 8) & 0xff;
    packet[1] = y & 0xff;
    packet[2] = hint;
    packet.set(rowBytes, 3);
    await send(encodePacket(0x85, packet));

    // Occasional yield keeps the BLE write queue from backing up on
    // longer labels (a 75 × 25 mm ring tag is ~200 rows).
    if ((y & (YIELD_EVERY - 1)) === 0) {
      await new Promise((r) => setTimeout(r, 4));
    }
  }
  await flushBlankRun();

  // -------- 3. Finalise --------
  await send(encodePacket(0xe3, [0x01])); // end page
  // Niimbot's own driver waits for a 0xA3 status callback to know the
  // paper has actually rolled through. Web Bluetooth's notification
  // timing across firmware versions is inconsistent, so we just sleep
  // proportional to the row count (~250 μs per row is comfortable on B21).
  const settleMs = Math.max(500, Math.round(srcH * 0.3));
  await new Promise((r) => setTimeout(r, settleMs));
  await send(encodePacket(0xf3, [0x01])); // end print
}
